import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createAssessmentSchema, createPaymentSchema } from "models/schemas";
import { GenericDraftWizard } from "../generic-draft-wizard";
import {
  createNoticeWizardConfig,
  SupersedingNoticeContext,
} from "./noticeWizardConfig";
import { INoticeDraftWizardProps } from "./definitions";
import { useLocalStorage } from "hooks/useLocalStorage";
import { GaiaObjectFormRef } from "../create-edit-gaiaobject-form/definitions";
import { useNotifications } from "features/notifications/hooks";
import { useCreateGaiaObjectMutation } from "../create-edit-gaiaobject-form/endpoints";
import { getGaiaObjectErrorMessage } from "../create-edit-gaiaobject-form/util";
import { useGetScanjobQuery } from "pages/draft-overview/endpoints";
import {
  useDownloadUrlQuery,
  useBatchRegisterDocumentsMutation,
} from "components/documents-table-os/endpoints";
import { useAppSelector } from "app/store";
import {
  useSelectedTeam,
  useDefaultDocumentType,
} from "features/team-settings/hooks";
import { useGaiaObjectUiContent } from "features/ui-content";
import { getCurrencySignFromCountry } from "features/ui-content/utils";
import { useGaiaObjectCustomAttributes } from "hooks/useGaiaObjectCustomAttributes";
import { processGaiaObjectForSubmission } from "utils/gaiaobject-utils";
import { GaiaConstants } from "utils/gaia-constants";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { Spinner, Alert, Box } from "@amzn/awsui-components-react";
import { useCreateAssessmentMutation } from "pages/create-assessment/endpoints";
import { useCreatePaymentMutation } from "pages/create-payment/endpoints";
import {
  useUpdatePaymentMutation,
  useLazyGetPaymentQuery,
} from "pages/edit-payment/endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import {
  registerScanDocument,
  updateScanStatus,
  createAssessmentFromData,
  createPaymentFromData,
  getNotificationConfig,
} from "utils/scan-job-utils";
import {
  useLazySearchGaiaObjectsQuery,
  SearchGaiaObjectResponse,
} from "./endpoints";
import { NewPaymentBreakdown } from "./definitions";
import { unixToDatePickerString } from "./utils";
import styles from "./style.module.scss";

const NoticeDraftWizard: React.FC<INoticeDraftWizardProps> = ({
  visible = true,
  onDismiss,
  initialData,
  category,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerWizardValidation, setTriggerWizardValidation] = useState(0);
  const [isFilePreviewVisible, setIsFilePreviewVisible] = useState(true);
  const [isAssessmentSimplified, setIsAssessmentSimplified] = useState(false);
  const [isPaymentSimplified, setIsPaymentSimplified] = useState(false);
  const [supersedingNotices, setSupersedingNotices] =
    useState<SearchGaiaObjectResponse | null>(null);
  const [isSearchingSuperseding, setIsSearchingSuperseding] = useState(false);
  const [zeroOutCheckedMap, setZeroOutCheckedMap] = useState<
    Record<string, boolean>
  >({});
  const formRef = useRef<GaiaObjectFormRef>(null);
  const { addNotification } = useNotifications();
  const [createGaiaObjectCall] = useCreateGaiaObjectMutation();
  const [createAssessment] = useCreateAssessmentMutation();
  const [createPayment] = useCreatePaymentMutation();
  const [updatePayment] = useUpdatePaymentMutation();
  const [getPayment] = useLazyGetPaymentQuery();
  const [batchRegisterDocumentsCall] = useBatchRegisterDocumentsMutation();
  const [updateScanJobCall] = useUpdateScanJobMutation();
  const [searchGaiaObjects] = useLazySearchGaiaObjectsQuery();
  const teamName = useAppSelector((state) => state.audits.teamName);
  const {
    isFileScanPaymentEnabled,
    isFileScanAssessmentEnabled,
    isSupersededPaymentsEnabled,
  } = useSelectedTeam();
  const documentType = useDefaultDocumentType(category);
  const navigate = useNavigate();
  const { shortId } = useParams<{ shortId: string }>();

  const localStorageKey = `notice-draft-wizard-step1-${shortId}`;
  const assessmentStorageKey = `assessment-step-${shortId}`;
  const paymentStorageKey = `payment-step-${shortId}`;
  const cleanupStorageKeys = useCallback(() => {
    localStorage.removeItem(localStorageKey);
    localStorage.removeItem(assessmentStorageKey);
    localStorage.removeItem(paymentStorageKey);
  }, [localStorageKey, assessmentStorageKey, paymentStorageKey]);
  const uiContent = useGaiaObjectUiContent(category);
  const { customAttributes } = useGaiaObjectCustomAttributes(category);

  const [storedFormData, setStoredFormData] = useLocalStorage<any>(
    localStorageKey,
    null,
  );

  // Assessment form control
  const { control, setValue } = useForm({
    mode: "onChange",
    resolver: zodResolver(createAssessmentSchema),
  });

  // Payment form control (for teams with FILE_SCAN_PAYMENT feature)
  const { control: paymentControl, setValue: paymentSetValue } = useForm({
    mode: "onChange",
    resolver: zodResolver(createPaymentSchema),
  });

  const {
    data: scanJobData,
    error: scanJobError,
    isLoading: scanJobLoading,
  } = useGetScanjobQuery(
    { shortId: shortId as string, trackOpen: false },
    { refetchOnMountOrArgChange: true, skip: !shortId },
  );

  const {
    data: downloadUrlData,
    error: downloadUrlError,
    isLoading: downloadUrlLoading,
    refetch: refetchDownloadUrl,
  } = useDownloadUrlQuery(
    { documentId: shortId as string, preview: true },
    {
      skip: !shortId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    },
  );

  useEffect(() => {
    return () => {
      if (shortId) {
        cleanupStorageKeys();
      }
    };
  }, [cleanupStorageKeys, shortId]);

  const processGaiaObjectData = useCallback(
    (data: any) => {
      return processGaiaObjectForSubmission(
        data,
        teamName,
        "notice",
        customAttributes,
        uiContent?.entityOptions,
      );
    },
    [customAttributes, teamName, uiContent?.entityOptions],
  );

  const [newBreakdowns, setNewBreakdowns] = useState<NewPaymentBreakdown[]>([]);

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);

    try {
      // Prepare and create notice
      const formDataToSubmit = {
        titleSuffix: storedFormData.titleSuffix,
        template: storedFormData.template,
        [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: {
          ...storedFormData[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
        },
      };

      const processedData = processGaiaObjectData(formDataToSubmit);
      const noticeResponse = await createGaiaObjectCall(processedData).unwrap();

      // Execute secondary operations and collect warnings
      const warnings: string[] = [];

      const documentError = await registerScanDocument(
        shortId,
        scanJobData,
        noticeResponse.shortId,
        teamName,
        batchRegisterDocumentsCall,
        documentType,
      );
      if (documentError) warnings.push(documentError);

      const scanStatusError = await updateScanStatus(
        shortId,
        noticeResponse.shortId,
        updateScanJobCall,
      );
      if (scanStatusError) warnings.push(scanStatusError);

      const assessmentResult = await createAssessmentFromData(
        noticeResponse.shortId,
        assessmentStorageKey,
        shortId,
        isAssessmentSimplified,
        createAssessment,
      );

      if (isFileScanPaymentEnabled) {
        const paymentResult = await createPaymentFromData(
          noticeResponse.shortId,
          paymentStorageKey,
          shortId,
          isPaymentSimplified,
          createPayment,
        );
        if (paymentResult.error) {
          warnings.push(`Payment creation: ${paymentResult.error}`);
        }
      }

      // Supersede only breakdowns where (taxYear, dueDate) match a new row.
      if (isSupersededPaymentsEnabled && supersedingNotices) {
        const newKeys = new Set<string>();
        for (const nb of newBreakdowns) {
          if (nb.dueDate && nb.taxYear) {
            newKeys.add(`${nb.taxYear}|${nb.dueDate.replace(/-/g, "/")}`);
          }
        }

        const breakdowns = (supersedingNotices.outcomeBreakdowns ?? []).filter(
          (b: any) => {
            if (b.superseded) return false;
            if (!(zeroOutCheckedMap[b.outcomeBreakdownId] ?? true))
              return false;
            const dueDateKey =
              typeof b.dueDate === "number"
                ? unixToDatePickerString(b.dueDate)
                : (b.dueDate?.replace(/-/g, "/") ?? "");
            // Service does not project taxYear; fall back to leading digits of category.
            const taxYearKey =
              b.taxYear ?? b.category?.match(/^\d{1,4}/)?.[0] ?? "";
            if (!taxYearKey || !dueDateKey) return false;
            return newKeys.has(`${taxYearKey}|${dueDateKey}`);
          },
        );

        const paymentGroups = new Map<
          string,
          { paymentId: string; gaiaObjectId: string; breakdownIds: string[] }
        >();
        for (const b of breakdowns) {
          if (!b.parentId || !b.gaiaObjectId) continue;

          const key = `${b.parentId}#${b.gaiaObjectId}`;
          if (!paymentGroups.has(key)) {
            paymentGroups.set(key, {
              paymentId: b.parentId,
              gaiaObjectId: b.gaiaObjectId,
              breakdownIds: [],
            });
          }
          paymentGroups.get(key)!.breakdownIds.push(b.outcomeBreakdownId);
        }
        for (const group of Array.from(paymentGroups.values())) {
          try {
            const paymentData = await getPayment({
              paymentId: group.paymentId,
              gaiaObjectId: group.gaiaObjectId,
            }).unwrap();

            const payment = paymentData.payment;
            const updatedBreakdowns = (payment.taxYearAreaBreakdown || []).map(
              (bd: any) => ({
                ...bd,
                superseded: group.breakdownIds.includes(bd.outcomeBreakdownId)
                  ? true
                  : bd.superseded,
                additionalInformation: group.breakdownIds.includes(
                  bd.outcomeBreakdownId,
                )
                  ? `Superseded by Notice ${noticeResponse.shortId}`
                  : bd.additionalInformation,
              }),
            );

            await updatePayment({
              paymentId: group.paymentId,
              gaiaObjectId: group.gaiaObjectId,
              type: payment.type,
              paymentDate: Number(payment.paymentDate),
              paymentRefNumber: payment.paymentRefNumber || "",
              trackingRefNumber: payment.trackingRefNumber || "",
              paymentAuthorizationLink: payment.paymentAuthorizationLink || "",
              notes: payment.notes || "",
              totalTaxAmount: String(payment.totalTaxAmount || "0"),
              totalPenaltyAmount: String(payment.totalPenaltyAmount || "0"),
              totalInterestAmount: String(payment.totalInterestAmount || "0"),
              totalOtherAmount: String(payment.totalOtherAmount || "0"),
              taxAreaBreakdown: payment.taxAreaBreakdown || [],
              taxYearBreakdown: payment.taxYearBreakdown || [],
              taxYearAreaBreakdown: updatedBreakdowns,
            }).unwrap();
          } catch (err) {
            console.error("Failed to supersede breakdowns:", err);
            warnings.push("Failed to mark old payments as superseded");
          }
        }
      }

      const notificationConfig = getNotificationConfig(
        processedData.title,
        assessmentResult,
        warnings,
      );

      addNotification({
        ...notificationConfig,
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });

      // Clean up and navigate
      cleanupStorageKeys();
      navigate("/notices");
      onDismiss?.();
    } catch (error: any) {
      console.error("Error creating notice:", error);
      const errorMessage = getGaiaObjectErrorMessage(
        error,
        "notice",
        error?.message || "An unexpected error occurred",
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    storedFormData,
    processGaiaObjectData,
    scanJobData,
    teamName,
    cleanupStorageKeys,
    shortId,
    assessmentStorageKey,
    paymentStorageKey,
    isAssessmentSimplified,
    isPaymentSimplified,
    isFileScanPaymentEnabled,
    isSupersededPaymentsEnabled,
    supersedingNotices,
    zeroOutCheckedMap,
    batchRegisterDocumentsCall,
    updateScanJobCall,
    createAssessment,
    createPayment,
    updatePayment,
    getPayment,
    createGaiaObjectCall,
    addNotification,
    navigate,
    onDismiss,
    documentType,
    newBreakdowns,
  ]);

  const handleCancel = useCallback(() => {
    cleanupStorageKeys();
    navigate("/drafts");
    onDismiss?.();
  }, [cleanupStorageKeys, navigate, onDismiss]);

  const stepAdvancementRef = useRef<((stepIndex: number) => void) | null>(null);

  const handleStepValidationSuccess = useCallback(
    (_data: any) => {
      // Trigger superseding notice check when moving from Step 1 to Step 2
      const customAttrs = _data?.customAttributesDataMap || {};
      const entity = customAttrs.entity
        ? String(customAttrs.entity).split("||")[0]
        : undefined;
      const taxType = Array.isArray(customAttrs.taxType)
        ? customAttrs.taxType[0]
        : customAttrs.taxType;
      const localJurisdiction = customAttrs.localJurisdiction;
      const registrationNumber = customAttrs.registrationNumber;

      if (isSupersededPaymentsEnabled && entity && taxType) {
        const tokens = [
          {
            propertyKey: "customAttributesDataMap.entity",
            operator: "=",
            value: entity,
          },
          {
            propertyKey: "customAttributesDataMap.taxType",
            operator: "=",
            value: taxType,
          },
          localJurisdiction && {
            propertyKey: "customAttributesDataMap.localJurisdiction",
            operator: "=",
            value: localJurisdiction,
          },
          registrationNumber && {
            propertyKey: "customAttributesDataMap.registrationNumber",
            operator: "=",
            value: registrationNumber,
          },
        ].filter(Boolean);

        setIsSearchingSuperseding(true);
        searchGaiaObjects({
          teamName,
          category: "notice",
          propertyFilterQuery: {
            operation: "and",
            tokens,
          },
          pageSize: 50,
          includeOutcomeBreakdowns: true,
        })
          .unwrap()
          .then((result) => {
            setSupersedingNotices(result);
          })
          .catch(() => {
            addNotification({
              type: "warning",
              header: "Failed to check for superseding notices",
              dismissible: true,
              timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
            });
          })
          .finally(() => {
            setIsSearchingSuperseding(false);
          });
      }

      if (stepAdvancementRef.current) {
        stepAdvancementRef.current(1);
      }
    },
    [searchGaiaObjects, teamName, isSupersededPaymentsEnabled, addNotification],
  );

  const triggerFormValidation = useCallback(() => {
    setTriggerWizardValidation((prev) => prev + 1);
  }, []);

  const currencySign = getCurrencySignFromCountry(
    storedFormData?.[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]?.country ?? "",
    uiContent,
  );

  const config = useMemo(
    () =>
      createNoticeWizardConfig(
        handleComplete,
        handleCancel,
        category,
        scanJobData,
        downloadUrlData,
        shortId,
        handleStepValidationSuccess,
        triggerWizardValidation,
        triggerFormValidation,
        formRef,
        isFilePreviewVisible,
        setIsFilePreviewVisible,
        isAssessmentSimplified,
        setIsAssessmentSimplified,
        control,
        setValue,
        refetchDownloadUrl,
        isPaymentSimplified,
        setIsPaymentSimplified,
        paymentControl,
        paymentSetValue,
        isFileScanPaymentEnabled,
        isFileScanAssessmentEnabled,
        setNewBreakdowns,
      ),
    [
      handleComplete,
      handleCancel,
      scanJobData,
      downloadUrlData,
      shortId,
      handleStepValidationSuccess,
      triggerWizardValidation,
      triggerFormValidation,
      isFilePreviewVisible,
      isAssessmentSimplified,
      isPaymentSimplified,
      control,
      setValue,
      paymentControl,
      paymentSetValue,
      refetchDownloadUrl,
      category,
      isFileScanPaymentEnabled,
      isFileScanAssessmentEnabled,
      setNewBreakdowns,
    ],
  );

  config.setStepAdvancementCallback = (
    callback: (stepIndex: number) => void,
  ) => {
    stepAdvancementRef.current = callback;
  };

  config.formRef = formRef;
  config.setStoredFormData = setStoredFormData;

  if (!visible) {
    return null;
  }

  const isLoading = scanJobLoading || downloadUrlLoading;
  if (isLoading) {
    return (
      <div
        className={styles.noticeDraftWizard}
        data-testid="notice-draft-wizard"
      >
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" padding={{ top: "m" }}>
            Loading data...
          </Box>
        </Box>
      </div>
    );
  }

  const hasError = scanJobError || downloadUrlError;
  if (hasError) {
    const errorMessage = scanJobError
      ? `Failed to load: ${(scanJobError as any)?.message || "Unknown error"}`
      : `Failed to preview: ${
          (downloadUrlError as any)?.message || "Unknown error"
        }`;

    return (
      <div
        className={styles.noticeDraftWizard}
        data-testid="notice-draft-wizard"
      >
        <Alert
          type="error"
          header="Failed to load data"
          action={
            <Box>
              <button onClick={handleCancel}>Return to Notices</button>
            </Box>
          }
        >
          {errorMessage}
        </Alert>
      </div>
    );
  }

  return (
    <SupersedingNoticeContext.Provider
      value={{
        supersedingNotices,
        newBreakdowns,
        currencySign: currencySign || "",
        zeroOutCheckedMap,
        setZeroOutCheckedMap,
        isSearching: isSearchingSuperseding,
      }}
    >
      <div
        className={styles.noticeDraftWizard}
        data-testid="notice-draft-wizard"
      >
        <GenericDraftWizard
          config={config}
          visible={visible}
          onDismiss={onDismiss}
          initialData={initialData}
          isSubmitting={isSubmitting}
        />
      </div>
    </SupersedingNoticeContext.Provider>
  );
};

export default NoticeDraftWizard;
